import { useRef, useState, type ReactNode } from 'react';
import { Button, Input } from '@dycs/ui';
import {
  QUOTE_KIND_LABELS,
  QUOTE_REFERRAL_OPTIONS,
  QUOTE_VEHICLE_TYPES,
  type QuoteKind,
} from '@dycs/shared';
import { callableMessage, submitQuote } from '@/lib/firebase';

/**
 * 전세버스 견적 접수 폼 (2026-10-18 way — 홈페이지 구글폼 2종 이관).
 *   칸은 원 폼 그대로 + 선택 칸 몇 개(인원·대수·운행 시작 희망일).
 *     통근·통학: 단체명 · 노선 지역 · 노선 개수 · 차량 종류 · 출발/도착 시간
 *     일반 관광: 차량 종류 · 출발 지역/일/시간 · 도착 지역/일/시간
 *   필수 판정은 서버(`buildQuoteDoc`)가 한 번 더 한다 — 여기는 안내용.
 *   🔴 휴대폰 형식 = 서버 `isKoreanMobile` 과 같은 자(01x) — 견적 회신은 전화로 간다.
 */
interface QuoteFormProps {
  tenantId: string;
  /** `?type=quote&kind=` 로 구분이 정해져 들어왔으면 그 값(토글은 그대로 보인다). */
  initialKind?: QuoteKind;
}

const SELECT =
  'w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none';

function phoneLooksOk(raw: string): boolean {
  let d = raw.replace(/[^0-9]/g, '');
  if (d.startsWith('820')) d = d.slice(2);
  else if (d.startsWith('82')) d = '0' + d.slice(2);
  return /^01[016789][0-9]{7,8}$/.test(d);
}

function todayKst(): string {
  return new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
}

/** 단계 머리 — 번호 동그라미 + 제목. 긴 폼을 덩어리로 끊어 «어디까지 왔나»를 보이게 한다. */
function Step({ no, title, children }: { no: number; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-accent text-accent-fg text-[11px] font-bold flex items-center justify-center">
          {no}
        </span>
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="space-y-3 pl-7">{children}</div>
    </section>
  );
}

function Chips({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(value === o ? '' : o)}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            value === o
              ? 'bg-accent text-accent-fg border-accent'
              : 'bg-surface text-text-secondary border-border-default hover:border-accent'
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function QuoteForm({ tenantId, initialKind }: QuoteFormProps) {
  const [kind, setKind] = useState<QuoteKind>(initialKind ?? 'commute');
  // 신청인
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  // 차량
  const [vehicleType, setVehicleType] = useState('');
  const [vehicleCount, setVehicleCount] = useState('');
  const [passengers, setPassengers] = useState('');
  // 통근
  const [routeRegion, setRouteRegion] = useState('');
  const [routeCount, setRouteCount] = useState('');
  const [startDate, setStartDate] = useState('');
  // 관광
  const [departRegion, setDepartRegion] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [arriveRegion, setArriveRegion] = useState('');
  const [arriveDate, setArriveDate] = useState('');
  // 공통 시간
  const [departTime, setDepartTime] = useState('');
  const [arriveTime, setArriveTime] = useState('');
  const [referral, setReferral] = useState('');
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const honeypot = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (done) {
    return (
      <div className="text-center py-8 space-y-2">
        <div className="text-3xl">🚌</div>
        <p className="text-sm font-semibold">견적 요청이 접수되었습니다.</p>
        <p className="text-xs text-text-tertiary">
          접수번호 <span className="font-mono text-text-secondary">{done.slice(0, 6).toUpperCase()}</span>
        </p>
        <p className="text-xs text-text-secondary leading-relaxed">
          담당자가 내용을 확인한 뒤 적어 주신 연락처로
          <br />
          견적을 안내해 드리겠습니다. 감사합니다.
        </p>
      </div>
    );
  }

  const isTour = kind === 'tour';
  const today = todayKst();

  function check(): string | null {
    if (isTour) {
      if (!departRegion.trim()) return '출발 지역을 입력해 주세요.';
      if (!departDate) return '출발일을 선택해 주세요.';
      if (!arriveRegion.trim()) return '도착 지역을 입력해 주세요.';
      if (arriveDate && arriveDate < departDate) return '도착일이 출발일보다 빠릅니다.';
    } else {
      if (!company.trim()) return '단체·회사·학교명을 입력해 주세요.';
      if (!routeRegion.trim()) return '노선 지역을 입력해 주세요.';
    }
    if (!vehicleType) return '차량 종류를 선택해 주세요.';
    if (!name.trim()) return '신청인 이름을 입력해 주세요.';
    if (!phoneLooksOk(phone)) return '연락 가능한 휴대폰 번호(010…)를 입력해 주세요.';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return '이메일 형식을 확인해 주세요.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const problem = check();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await submitQuote({
        tenantId,
        kind,
        customerName: name.trim(),
        customerPhone: phone.trim(),
        customerEmail: email.trim() || undefined,
        companyName: company.trim() || undefined,
        vehicleType,
        vehicleCount: vehicleCount ? Number(vehicleCount) : undefined,
        passengers: passengers ? Number(passengers) : undefined,
        ...(isTour
          ? {
              departRegion: departRegion.trim(),
              departDate,
              arriveRegion: arriveRegion.trim(),
              arriveDate: arriveDate || undefined,
            }
          : {
              routeRegion: routeRegion.trim(),
              routeCount: routeCount.trim() || undefined,
              startDate: startDate || undefined,
            }),
        departTime: departTime || undefined,
        arriveTime: arriveTime || undefined,
        referral: referral || undefined,
        note: note.trim() || undefined,
        privacyConsent: true,
        website: honeypot.current?.value || undefined,
      });
      setDone(res.quoteId);
    } catch (err) {
      console.error('[widget] 견적 접수 실패', err);
      setError(callableMessage(err, '접수에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      {/* 구분 토글 — 칸 구성이 통째로 바뀐다(입력값은 구분별로 따로 살아 있다). */}
      <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted">
        {(['commute', 'tour'] as QuoteKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={`py-2 rounded-md text-sm font-medium transition-colors ${
              kind === k ? 'bg-surface text-text-primary shadow-1' : 'text-text-tertiary hover:text-text-primary'
            }`}
          >
            {k === 'commute' ? '🏢 ' : '🧳 '}
            {QUOTE_KIND_LABELS[k]}
          </button>
        ))}
      </div>
      <p className="-mt-3 text-xs text-text-tertiary leading-relaxed">
        {isTour
          ? '워크숍·수학여행·행사 등 하루~며칠 운행하는 전세버스 견적입니다.'
          : '임직원 출퇴근·학생 통학처럼 매일 같은 노선을 도는 정기 운행 견적입니다.'}
      </p>

      <Step no={1} title="운행 정보">
        {isTour ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input label="출발 지역" required placeholder="예: 서울 강남역" value={departRegion} onChange={(e) => setDepartRegion(e.target.value)} />
              <Input label="도착 지역" required placeholder="예: 강원 속초" value={arriveRegion} onChange={(e) => setArriveRegion(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="출발일" required type="date" min={today} value={departDate} onChange={(e) => setDepartDate(e.target.value)} />
              <Input label="출발 시간" type="time" value={departTime} onChange={(e) => setDepartTime(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="도착(복귀)일" type="date" min={departDate || today} value={arriveDate} onChange={(e) => setArriveDate(e.target.value)} />
              <Input label="도착 시간" type="time" value={arriveTime} onChange={(e) => setArriveTime(e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <Input label="단체 · 회사 · 학교명" required placeholder="예: 동영전자 판교사옥" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Input label="노선 지역" required placeholder="예: 수원·동탄 → 판교" value={routeRegion} onChange={(e) => setRouteRegion(e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <Input label="노선 개수" placeholder="예: 3개" value={routeCount} onChange={(e) => setRouteCount(e.target.value)} />
              <Input label="운행 시작 희망일" type="date" min={today} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="출발 시간" type="time" value={departTime} onChange={(e) => setDepartTime(e.target.value)} />
              <Input label="도착 시간" type="time" value={arriveTime} onChange={(e) => setArriveTime(e.target.value)} />
            </div>
          </>
        )}
      </Step>

      <Step no={2} title="차량">
        <div>
          <span className="block mb-1.5 text-xs font-medium text-text-secondary">
            차량 종류<span className="text-danger"> *</span>
          </span>
          <Chips options={QUOTE_VEHICLE_TYPES} value={vehicleType} onChange={setVehicleType} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="차량 대수" type="number" inputMode="numeric" min={1} placeholder="예: 2" value={vehicleCount} onChange={(e) => setVehicleCount(e.target.value)} />
          <Input label="탑승 인원" type="number" inputMode="numeric" min={1} placeholder="예: 80" value={passengers} onChange={(e) => setPassengers(e.target.value)} />
        </div>
      </Step>

      <Step no={3} title="신청인">
        <div className="grid grid-cols-2 gap-2">
          <Input label="이름" required placeholder="홍길동" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="휴대폰" required type="tel" inputMode="tel" placeholder="010-0000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        {isTour && (
          <Input label="단체 · 회사 · 학교명" placeholder="예: ○○고등학교 2학년" value={company} onChange={(e) => setCompany(e.target.value)} />
        )}
        <Input label="이메일 (선택)" type="email" inputMode="email" placeholder="견적서를 메일로 받으시려면" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Step>

      <Step no={4} title="요청사항">
        <textarea
          className={`${SELECT} min-h-24 placeholder:text-text-disabled`}
          placeholder="경유지, 대기 시간, 왕복 여부 등 견적에 필요한 내용을 자유롭게 적어 주세요."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <label className="block">
          <span className="block mb-1 text-xs font-medium text-text-secondary">동영관광을 어떻게 알게 되셨나요? (선택)</span>
          <select className={SELECT} value={referral} onChange={(e) => setReferral(e.target.value)}>
            <option value="">선택 안 함</option>
            {QUOTE_REFERRAL_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </Step>

      {/* 봇 덫 — 사람에겐 안 보인다(스크린리더도 건너뛴다). */}
      <input
        ref={honeypot}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] w-px h-px opacity-0"
      />

      <label className="flex items-start gap-2 text-xs text-text-secondary">
        <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>
          개인정보(이름·연락처·이메일) 수집·이용에 동의합니다. 견적 안내 목적으로만 쓰며, 상담 종료 후 관련
          법령에 따라 보관·파기합니다.
        </span>
      </label>

      {error && <p className="text-xs text-danger">{error}</p>}

      <Button full size="lg" type="submit" disabled={!consent} loading={submitting}>
        견적 요청하기
      </Button>
    </form>
  );
}
